import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ARTICLES_BY_SLUG } from '../data/articles';

// Brief native-language notes (client decision 2026-07): the public site is
// English-only. These discreet subpages exist so search engines can index the
// correct native keywords for each market. Each page is intentionally short and
// links to the full English country page, where the claim form lives.
// The old short URLs (/de, /fr, ...) redirect to the English country pages.
// Tone: institutional, deontologically compliant (no guarantees, no "specialist"
// claims, no temporal promises, no collection-agency terminology).
// Transparency: correspondence is handled in English or Italian.

const BASE = 'https://belliardo-international.com';

// Old short URLs now redirect to the corresponding English country page.
export const NATIVE_REDIRECTS = {
  de: '/germany', fr: '/france', es: '/spain', nl: '/netherlands', ar: '/uae', el: '/greece',
};

export const NATIVE_NOTES = {
  de: {
    code: 'de', dir: 'ltr', countryCode: 'germany', countryPath: '/germany', path: '/germany/de',
    linkLabel: 'Deutsche Kurzfassung',
    articleSlug: 'european-order-for-payment-german-creditors-italy',
    metaTitle: 'Forderungseinzug in Italien — Anwalt in Verona | Studio Legale Belliardo',
    metaDesc: 'Italienischer Kunde zahlt nicht? Studio Legale Belliardo in Verona prüft Ihre Forderung und leitet das geeignete Verfahren in Italien ein — kostenlose Ersteinschätzung.',
    kicker: 'Internationale Praxis · Deutschland',
    h1: 'Forderungseinzug in Italien für deutsche Unternehmen',
    intro: 'Deutschland und Italien verbindet einer der intensivsten Handelskorridore Europas. Wenn Rechnungen unbezahlt bleiben oder ein deutscher Titel gegen italienisches Vermögen vollstreckt werden muss, übernimmt unsere italienische Praxis den lokalen Teil — Forderungseinzug in Verona, Mailand, Rom und ganz Italien.',
    stepsTitle: 'Das Verfahren in Kürze',
    steps: [
      'Kostenlose Ersteinschätzung — Sie senden uns Vertrag, Rechnungen und Korrespondenz; wir erstellen eine erste schriftliche Einschätzung zu Verfahrensweg, Ablauf und Kostenrahmen — kostenlos.',
      'Formelles Mahnschreiben nach italienischem Recht, per PEC oder Einschreiben an den Schuldner, mit dem Ziel der freiwilligen Zahlung vor einem Gerichtsverfahren.',
      'Klage und Zwangsvollstreckung in Italien — decreto ingiuntivo, ordentliche Klage und Vollstreckung (esecuzione forzata) vor dem zuständigen italienischen Gericht.',
    ],
    docsTitle: 'Diese Unterlagen benötigen wir',
    docs: ['Vertrag oder Bestellung', 'Unbezahlte Rechnungen', 'Liefer- und Frachtdokumente (z. B. CMR)', 'Geschäftskorrespondenz, insbesondere Schuldanerkenntnisse', 'Zahlungshistorie'],
    timingTitle: 'Realistischer Ablauf',
    timing: 'Die Dauer hängt vom Gericht, der Beweislage und davon ab, ob der Schuldner Widerspruch einlegt. In geeigneten Fällen kann ein Zahlungsbefehl deutlich schneller ergehen als ein Urteil im ordentlichen Verfahren; die Vollstreckung richtet sich nach der Vermögenslage des Schuldners. Eine Erfolgsgarantie ist nach den Berufsregeln nicht zulässig — Sie erhalten stattdessen eine ehrliche, dokumentierte Ersteinschätzung.',
    jBlurb: 'Unser ausführlicher Leitfaden zum Europäischen Zahlungsbefehl (EOP) und zum Vergleich mit dem italienischen decreto ingiuntivo.',
    jRead: 'Den vollständigen Beitrag auf Englisch lesen →',
    ctaToFull: 'Vollständige englische Version mit Anfrageformular',
    ctaNote: 'Die Korrespondenz erfolgt in englischer oder italienischer Sprache. Das Anfrageformular ist in englischer Sprache gehalten.',
  },
  fr: {
    code: 'fr', dir: 'ltr', countryCode: 'france', countryPath: '/france', path: '/france/fr',
    linkLabel: 'Version française',
    articleSlug: 'enforcing-french-judgment-italy-injonction-de-payer',
    metaTitle: 'Recouvrement de créances en Italie — Avocat à Vérone | Studio Legale Belliardo',
    metaDesc: 'Votre client italien ne paie pas ? Le Studio Legale Belliardo à Vérone examine votre créance et engage la procédure adaptée en Italie — évaluation initiale gratuite.',
    kicker: 'Pratique internationale · France',
    h1: 'Recouvrement de créances en Italie pour les entreprises françaises',
    intro: "La France compte parmi les premiers partenaires commerciaux de l'Italie. Lorsque la discipline de paiement transfrontalière se dégrade, notre pratique italienne prend en charge le volet local et le porte devant les tribunaux. Recouvrement de créances à Vérone, Milan, Rome et dans toute l'Italie.",
    stepsTitle: 'La procédure en bref',
    steps: [
      'Évaluation initiale gratuite — vous nous transmettez contrat, factures et correspondance ; nous vous adressons une première évaluation écrite — voie procédurale, délais prévisibles et estimation des coûts — sans frais.',
      'Mise en demeure conforme au droit italien, adressée au débiteur par PEC ou courrier recommandé, afin d\u2019obtenir le paiement volontaire avant toute procédure.',
      'Action et exécution en Italie — decreto ingiuntivo, action au fond et exécution forcée (esecuzione forzata) devant le tribunal italien compétent.',
    ],
    docsTitle: 'Les pièces à fournir',
    docs: ['Contrat ou bon de commande', 'Factures impayées', 'Documents de livraison et de transport (CMR, etc.)', 'Correspondance commerciale, notamment toute reconnaissance de dette', 'Historique des paiements'],
    timingTitle: 'Déroulement réaliste',
    timing: "Le délai dépend du tribunal, des preuves disponibles et d'une éventuelle opposition du débiteur. Lorsque les conditions sont réunies, une injonction de payer peut être rendue plus rapidement qu'un jugement au fond dans une procédure ordinaire ; l'exécution dépend de la situation patrimoniale du débiteur. Conformément aux règles déontologiques, aucune garantie de résultat ne peut être donnée — vous recevez une évaluation initiale honnête et documentée.",
    jBlurb: "Notre guide complet : de l'injonction de payer française à l'exécution en Italie.",
    jRead: 'Lire la note complète en anglais →',
    ctaToFull: 'Version anglaise complète avec formulaire de demande',
    ctaNote: 'La correspondance est traitée en anglais ou en italien. Le formulaire de demande est rédigé en anglais.',
  },
  es: {
    code: 'es', dir: 'ltr', countryCode: 'spain', countryPath: '/spain', path: '/spain/es',
    linkLabel: 'Versión en español',
    articleSlug: 'recovering-debt-italy-spanish-creditors',
    metaTitle: 'Recupero de deudas en Italia — Abogado en Verona | Studio Legale Belliardo',
    metaDesc: '¿Su cliente italiano no paga? El Studio Legale Belliardo en Verona examina su crédito e inicia el procedimiento adecuado en Italia — evaluación inicial gratuita.',
    kicker: 'Práctica internacional · España',
    h1: 'Recupero de deudas en Italia para empresas españolas',
    intro: 'El derecho civil español e italiano comparten raíces profundas, pero el procedimiento, los plazos y la ejecución divergen notablemente. Nuestra práctica italiana gestiona el tramo local — coordinada con su abogado cuando sea necesario. Recuperación de deudas en Verona, Milán, Roma y toda Italia.',
    stepsTitle: 'El procedimiento en síntesis',
    steps: [
      'Evaluación inicial gratuita — envíenos el contrato, las facturas y la correspondencia; le entregamos una primera evaluación escrita — vía procesal, plazos previstos y estimación de costes — sin coste.',
      'Requerimiento de pago conforme al derecho italiano, enviado al deudor por PEC o correo certificado, con el fin de obtener el pago voluntario antes de cualquier litigio.',
      'Acción y ejecución en Italia — decreto ingiuntivo, demanda ordinaria y ejecución forzosa (esecuzione forzata) ante el tribunal italiano competente.',
    ],
    docsTitle: 'Documentación necesaria',
    docs: ['Contrato u orden de compra', 'Facturas impagadas', 'Documentos de entrega y transporte (CMR, etc.)', 'Correspondencia comercial, en particular cualquier reconocimiento de deuda', 'Historial de pagos'],
    timingTitle: 'Desarrollo realista',
    timing: 'El plazo depende del tribunal, de las pruebas disponibles y de una posible oposición del deudor. Cuando se dan los requisitos, el mandamiento de pago puede dictarse con mayor rapidez que una sentencia en un proceso ordinario; la ejecución depende del patrimonio del deudor. Conforme a las normas deontológicas no es posible garantizar el resultado — usted recibe una evaluación inicial honesta y documentada.',
    jBlurb: 'Nuestra guía completa: proceso monitorio, orden europea de pago o decreto ingiuntivo — cómo elegir la vía correcta.',
    jRead: 'Leer la nota completa en inglés →',
    ctaToFull: 'Versión completa en inglés con formulario de solicitud',
    ctaNote: 'La correspondencia se tramita en inglés o italiano. El formulario de solicitud está redactado en inglés.',
  },
  nl: {
    code: 'nl', dir: 'ltr', countryCode: 'netherlands', countryPath: '/netherlands', path: '/netherlands/nl',
    linkLabel: 'Nederlandse versie',
    articleSlug: 'enforcing-dutch-judgment-italy-brussels-I-bis',
    metaTitle: 'Incasso in Italië — Advocatenkantoor in Verona | Studio Legale Belliardo',
    metaDesc: 'Uw Italiaanse klant betaalt niet? Studio Legale Belliardo in Verona beoordeelt uw vordering en start de passende procedure in Italië — gratis eerste beoordeling.',
    kicker: 'Internationale praktijk · Nederland',
    h1: 'Incasso in Italië voor Nederlandse ondernemingen',
    intro: 'Een logistiek intensieve corridor met de Italiaanse markt maakt Nederlandse ondernemingen tot regelmatige schuldeisers van Italiaanse tegenpartijen. Onze Italiaanse praktijk behandelt het lokale incasso van begin tot eind — incasso in Verona, Milaan, Rome en heel Italië.',
    stepsTitle: 'De procedure in het kort',
    steps: [
      'Gratis eerste beoordeling — u stuurt ons contract, facturen en correspondentie; wij sturen een eerste schriftelijke beoordeling — procedurekeuze, verwachte doorlooptijd en kostenraming — kosteloos.',
      'Formele aanmaning volgens Italiaans recht, per PEC of aangetekende brief aan de schuldenaar, gericht op vrijwillige betaling vóór een procedure.',
      'Procedure en tenuitvoerlegging in Italië — decreto ingiuntivo, bodemprocedure en gedwongen tenuitvoerlegging (esecuzione forzata) bij de bevoegde Italiaanse rechtbank.',
    ],
    docsTitle: 'Benodigde stukken',
    docs: ['Contract of bestelling', 'Onbetaalde facturen', 'Leverings- en transportdocumenten (bijv. CMR)', 'Zakelijke correspondentie, met name erkenningen van de schuld', 'Betalingshistorie'],
    timingTitle: 'Realistisch verloop',
    timing: 'De doorlooptijd hangt af van de rechtbank, de bewijslast en een eventueel verzet van de schuldenaar. In geschikte gevallen kan een betalingsbevel aanzienlijk eerder worden uitgevaardigd dan een vonnis in een gewone procedure; de tenuitvoerlegging hangt af van de vermogenspositie van de schuldenaar. Een resultaatgarantie is op grond van de beroepsregels niet toegestaan — u ontvangt een eerlijke, gedocumenteerde eerste beoordeling.',
    jBlurb: 'Onze volledige gids: een Nederlands vonnis ten uitvoer leggen in Italië onder Brussel I-bis.',
    jRead: 'Lees de volledige notitie in het Engels →',
    ctaToFull: 'Volledige Engelse versie met aanvraagformulier',
    ctaNote: 'De correspondentie wordt in het Engels of Italiaans gevoerd. Het aanvraagformulier is in het Engels opgesteld.',
  },
  ar: {
    code: 'ar', dir: 'rtl', countryCode: 'uae', countryPath: '/uae', path: '/uae/ar',
    linkLabel: 'النسخة العربية',
    articleSlug: 'recovering-unpaid-invoices-italy-uae-businesses',
    metaTitle: 'تحصيل الديون في إيطاليا — Studio Legale Belliardo, فيرونا',
    metaDesc: 'عميلك الإيطالي لا يسدد؟ مكتب Studio Legale Belliardo في فيرونا يدرس مطالبتك ويباشر الإجراء القضائي المناسب في إيطاليا — تقييم أولي مجاني.',
    kicker: 'الممارسة الدولية · الإمارات العربية المتحدة',
    h1: 'تحصيل المطالبات في إيطاليا للشركات الإماراتية',
    intro: 'عندما تواجه شركتك الإماراتية طرفاً إيطالياً لا يسدد، فإن الاختصاص القضائي والإجراءات والتنفيذ ستكون غالباً في إيطاليا — مع مراعاة أي شرط اختصاص قضائي أو تحكيم في عقدكم. ممارستنا إيطالية. نعرف المحاكم والموظفين والإيقاع. تحصيل الديون في فيرونا وميلانو وروما وكامل إيطاليا.',
    stepsTitle: 'الإجراء باختصار',
    steps: [
      'تقييم أولي مجاني — أرسل لنا العقد والفواتير والمراسلات؛ نقدم تقييماً أولياً مكتوباً — المسار الإجرائي والإطار الزمني المتوقع وتقدير التكاليف — دون مقابل.',
      'إنذار رسمي وفق القانون الإيطالي يُرسل إلى المدين عبر البريد الإلكتروني المعتمد (PEC) أو البريد المسجل، بهدف الحصول على السداد الطوعي قبل أي تقاضٍ.',
      'الدعوى والتنفيذ في إيطاليا — decreto ingiuntivo أو الدعوى العادية والتنفيذ الجبري (esecuzione forzata) أمام المحكمة الإيطالية المختصة.',
    ],
    docsTitle: 'المستندات المطلوبة',
    docs: ['عقد أو طلب شراء', 'فواتير غير مسددة', 'مستندات التسليم والنقل (مثل CMR)', 'المراسلات التجارية، ولا سيما أي إقرار بالدين', 'سجل المدفوعات'],
    timingTitle: 'الإطار الواقعي للإجراء',
    timing: 'تعتمد المدة على المحكمة وعلى الأدلة المتاحة وعلى ما إذا كان المدين سيعترض. وفي الحالات المستوفية للشروط يمكن أن يصدر أمر الأداء بسرعة أكبر مقارنة بالحكم الصادر في دعوى عادية؛ أما التنفيذ فيتوقف على الوضع المالي للمدين. ووفقاً لقواعد المهنة لا يجوز ضمان النتيجة — بل تحصل على تقييم أولي صادق وموثق.',
    jBlurb: 'دليلنا الكامل: كيف تحصل الشركات الإماراتية على فواتيرها غير المسددة في إيطاليا.',
    jRead: 'اقرأ المقال الكامل بالإنجليزية →',
    ctaToFull: 'النسخة الإنجليزية الكاملة مع نموذج الطلب',
    ctaNote: 'تتم المراسلات باللغة الإنجليزية أو الإيطالية. نموذج الطلب متاح باللغة الإنجليزية.',
  },
  el: {
    code: 'el', dir: 'ltr', countryCode: 'greece', countryPath: '/greece', path: '/greece/el',
    linkLabel: 'Ελληνική έκδοση',
    articleSlug: 'decreto-ingiuntivo-explained-foreign-creditors',
    metaTitle: 'Είσπραξη απαιτήσεων στην Ιταλία — Studio Legale Belliardo, Βερόνα',
    metaDesc: 'Ο ιταλός πελάτης σας δεν πληρώνει; Το Studio Legale Belliardo στη Βερόνα εξετάζει την απαίτησή σας και κινεί την κατάλληλη διαδικασία στην Ιταλία — δωρεάν αρχική αξιολόγηση.',
    kicker: 'Διεθνής πρακτική · Ελλάδα',
    h1: 'Είσπραξη απαιτήσεων στην Ιταλία για ελληνικές επιχειρήσεις',
    intro: 'Το εμπόριο Ελλάδας και Ιταλίας κινείται ταχέως μέσω της Αδριατικής. Όταν η πειθαρχία πληρωμών καταρρέει και η δικαιοδοσία παραμένει ιταλική, η ιταλική μας πρακτική αναλαμβάνει την τοπική είσπραξη οφειλών — στη Βερόνα, στο Μιλάνο, στη Ρώμη και σε όλη την Ιταλία — από την εξώδικη δήλωση έως την εκτέλεση.',
    stepsTitle: 'Η διαδικασία συνοπτικά',
    steps: [
      'Δωρεάν αρχική αξιολόγηση — μας στέλνετε τη σύμβαση, τα τιμολόγια και την αλληλογραφία· σας στέλνουμε γραπτή αρχική αξιολόγηση — διαδικαστική οδό, αναμενόμενο χρονοδιάγραμμα και εκτίμηση κόστους — δωρεάν.',
      'Επίσημη εξώδικη δήλωση σύμφωνα με το ιταλικό δίκαιο, απεσταλμένη στον οφειλέτη μέσω PEC ή συστημένης επιστολής, με στόχο την εκούσια πληρωμή πριν από οποιαδήποτε δικαστική διαδικασία.',
      'Αγωγή και αναγκαστική εκτέλεση στην Ιταλία — decreto ingiuntivo, τακτική αγωγή και αναγκαστική εκτέλεση (esecuzione forzata) ενώπιον του αρμόδιου ιταλικού δικαστηρίου.',
    ],
    docsTitle: 'Απαραίτητα έγγραφα',
    docs: ['Σύμβαση ή παραγγελία', 'Απλήρωτα τιμολόγια', 'Έγγραφα παράδοσης και μεταφοράς (π.χ. CMR)', 'Εμπορική αλληλογραφία, ιδίως κάθε αναγνώριση του χρέους', 'Ιστορικό πληρωμών'],
    timingTitle: 'Ρεαλιστική πορεία',
    timing: 'Η διάρκεια εξαρτάται από το δικαστήριο, τα διαθέσιμα αποδεικτικά στοιχεία και την ενδεχόμενη αντίθεση του οφειλέτη. Όταν πληρούνται οι προϋποθέσεις, η διαταγή πληρωμής μπορεί να εκδοθεί ταχύτερα από μια απόφαση σε τακτική διαδικασία· η εκτέλεση εξαρτάται από την περιουσιακή κατάσταση του οφειλέτη. Σύμφωνα με τους δεοντολογικούς κανόνες δεν επιτρέπεται εγγύηση αποτελέσματος — λαμβάνετε μια ειλικρινή, τεκμηριωμένη αρχική αξιολόγηση.',
    jBlurb: 'Ο πλήρης οδηγός μας για το decreto ingiuntivo: η γρήγορη ιταλική οδός για ξένους δανειστές.',
    jRead: 'Διαβάστε το πλήρες άρθρο στα αγγλικά →',
    ctaToFull: 'Πλήρης αγγλική έκδοση με φόρμα αιτήματος',
    ctaNote: 'Η αλληλογραφία διεξάγεται στα αγγλικά ή ιταλικά. Η φόρμα αιτήματος είναι στα αγγλικά.',
  },
};

// Lookup by country code for the discreet footer link on each English country page.
export const NATIVE_BY_COUNTRY = Object.fromEntries(
  Object.entries(NATIVE_NOTES).map(([lang, n]) => [n.countryCode, { lang, path: n.path, linkLabel: n.linkLabel }])
);

function BriefSection({ title, children, testid }) {
  return (
    <section className="border-t border-white/10 py-12 md:py-16" data-testid={testid}>
      <div className="mx-auto max-w-[900px] px-6 md:px-10">
        <h2 className="font-serif text-2xl md:text-3xl text-white font-light leading-tight mb-8">{title}</h2>
        {children}
      </div>
    </section>
  );
}

export default function NativeLanding({ lang }) {
  const L = NATIVE_NOTES[lang];
  const art = ARTICLES_BY_SLUG[L.articleSlug];

  return (
    <div className="relative bg-ink-950 text-white min-h-screen" data-testid={`native-note-${lang}`}>
      <Helmet htmlAttributes={{ lang: L.code, dir: L.dir }}>
        <title>{L.metaTitle}</title>
        <meta name="description" content={L.metaDesc} />
        <link rel="canonical" href={`${BASE}${L.path}`} />
        <link rel="alternate" hrefLang={L.code} href={`${BASE}${L.path}`} />
        <link rel="alternate" hrefLang="en" href={`${BASE}${L.countryPath}`} />
      </Helmet>

      <header className="border-b border-white/8 bg-ink-950/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[900px] px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to={L.countryPath} className="font-serif text-xl text-white leading-none" data-testid={`native-note-home-${lang}`}>
            Belliardo<span className="font-mono text-white/40 text-[10px] ml-3 uppercase tracking-[0.25em]">International practice · Verona</span>
          </Link>
          <Link to={L.countryPath} className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white" data-testid={`native-note-full-link-${lang}`}>
            English version →
          </Link>
        </div>
      </header>

      <main dir={L.dir}>
        <section className="mx-auto max-w-[900px] px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40 mb-6">— {L.kicker}</p>
          <h1 className="font-serif text-3xl md:text-5xl leading-[1.08] tracking-tight text-white font-light mb-8">{L.h1}</h1>
          <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-[700px]">{L.intro}</p>
        </section>

        <BriefSection title={L.stepsTitle} testid={`native-note-steps-${lang}`}>
          <ol className="space-y-5">
            {L.steps.map((s, i) => (
              <li key={i} className="flex gap-5 items-start">
                <span className="font-mono text-[11px] text-white/50 tracking-[0.2em] pt-1 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-white/75 text-base leading-relaxed">{s}</p>
              </li>
            ))}
          </ol>
        </BriefSection>

        <BriefSection title={L.docsTitle} testid={`native-note-docs-${lang}`}>
          <ul className="space-y-3 mb-10">
            {L.docs.map((d) => (
              <li key={d} className="text-white/75 text-base leading-relaxed flex gap-4">
                <span className="text-white/50 shrink-0">—</span>{d}
              </li>
            ))}
          </ul>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mb-4">{L.timingTitle}</h3>
          <p className="text-white/75 text-base leading-relaxed max-w-[700px]">{L.timing}</p>
        </BriefSection>

        {art && (
          <section className="border-t border-white/10 py-12 md:py-16" data-testid={`native-note-journal-${lang}`}>
            <div className="mx-auto max-w-[900px] px-6 md:px-10">
              <p className="text-white/75 text-base leading-relaxed max-w-[700px] mb-6">{L.jBlurb}</p>
              <Link to={`/articles/${art.slug}`} className="group inline-block" data-testid={`native-note-article-link-${lang}`}>
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50 group-hover:text-white transition-colors">{L.jRead}</span>
              </Link>
            </div>
          </section>
        )}

        <section className="border-t border-white/10 py-12 md:py-16">
          <div className="mx-auto max-w-[900px] px-6 md:px-10">
            <div className="border border-white/15 bg-ink-900 p-8 md:p-10" data-testid={`native-note-cta-${lang}`}>
              <Link to={L.countryPath}
                className="inline-block font-mono text-[12px] uppercase tracking-[0.25em] bg-white text-ink-950 px-8 py-4 hover:bg-white/85 transition-colors"
                data-testid={`native-note-cta-button-${lang}`}>
                {L.ctaToFull} →
              </Link>
              <p className="text-white/50 text-sm mt-5 leading-relaxed">{L.ctaNote}</p>
            </div>
          </div>
        </section>

        <div className="border-t border-white/10" dir="ltr">
          <div className="mx-auto max-w-[900px] px-6 md:px-10 py-10 flex flex-wrap gap-x-8 gap-y-3 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
            <Link to={L.countryPath} className="hover:text-white transition-colors">English version</Link>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid={`native-note-privacy-${lang}`}>Privacy Policy</Link>
          </div>
          <div className="mx-auto max-w-[900px] px-6 md:px-10 pb-10 text-[11px] font-mono uppercase tracking-[0.2em] text-white/30">
            Studio Legale Belliardo · Salita Fontana del Ferro 24, 37129 Verona · P.IVA 03995680232
          </div>
        </div>
      </main>
    </div>
  );
}
